import * as React from "react";
import {
  CircleAlert,
  Maximize2,
  Minimize2,
  Pause,
  Play,
  RefreshCw,
  RotateCcw,
  RotateCw,
} from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * The console's one video player. Appearance is `.player` in `styles/ui.css`.
 *
 * ── Nothing is fetched until somebody presses play ──────────────────────────
 *
 * Until then this is a poster and a play button, so a visit record carrying
 * three sweeps costs three thumbnails rather than three clips. A 30-second
 * sweep is tens of megabytes and a reviewer opens one of them; auto-loading all
 * three is a bill and a slow page in exchange for nothing.
 *
 * ── Controls are CSS, not a timer ───────────────────────────────────────────
 *
 * They appear on hover, on keyboard focus, while paused, or pinned by a tap -
 * which is why there is no `mousemove` handler re-rendering React at 60Hz to
 * decide whether a bar is visible.
 *
 * ── The frame is fixed, the clip's shape is not ─────────────────────────────
 *
 * A technician sweeping an area holds the phone whichever way the work is, so
 * a record carries both landscape and portrait clips. The box takes one of two
 * fixed shapes - 16:9 or 3:4 - chosen from the media's own dimensions, and the
 * clip is letterboxed inside it rather than cropped. A fixed box keeps the card
 * from resizing when the clip loads; `contain` keeps the evidence whole, which
 * matters more here than a tidy fill: the hazard may be at the edge of frame.
 */

const SPEEDS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2];
const SKIP_SECONDS = 10;

export interface VideoPlayerProps {
  src: string;
  /** The still shown before play, and behind the clip while it loads. */
  poster?: string | null;
  className?: string;
  /** Skip the poster gate and start immediately. */
  autoPlay?: boolean;
  muted?: boolean;
  label?: string;
}

/** `1:04:07` past an hour, `4:07` under it. */
function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const s = Math.floor(seconds % 60);
  const m = Math.floor((seconds / 60) % 60);
  const h = Math.floor(seconds / 3600);
  const mm = h ? String(m).padStart(2, "0") : String(m);
  return `${h ? `${h}:` : ""}${mm}:${String(s).padStart(2, "0")}`;
}

export function VideoPlayer({
  src,
  poster,
  className,
  autoPlay = false,
  muted = false,
  label = "the recorded sweep",
}: VideoPlayerProps) {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const boxRef = React.useRef<HTMLDivElement>(null);
  const speedRef = React.useRef<HTMLDivElement>(null);

  // The clip is only fetched once this opens - a click on the poster, or `autoPlay`.
  const [started, setStarted] = React.useState(autoPlay);
  const [playing, setPlaying] = React.useState(false);
  const [waiting, setWaiting] = React.useState(false);
  /** False from the moment the clip is requested until it holds enough data to play. */
  const [ready, setReady] = React.useState(false);
  const [time, setTime] = React.useState(0);
  const [duration, setDuration] = React.useState(0);
  const [buffered, setBuffered] = React.useState(0);
  const [rate, setRate] = React.useState(1);
  const [fullscreen, setFullscreen] = React.useState(false);
  const [speedOpen, setSpeedOpen] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  const [posterFailed, setPosterFailed] = React.useState(false);
  // Touch has no hover, so a tap pins the controls open.
  const [pinned, setPinned] = React.useState(false);
  const [hoverAt, setHoverAt] = React.useState<number | null>(null);
  /**
   * Read off the poster before play and off the clip's own metadata after, so
   * the frame settles on the right shape from the still rather than jumping
   * when the video finally loads.
   */
  const [upright, setUpright] = React.useState(false);

  const pct = (v: number) => (duration > 0 ? `${Math.min(100, (v / duration) * 100)}%` : "0%");

  React.useEffect(() => {
    setStarted(autoPlay);
    setPlaying(false);
    setWaiting(false);
    setReady(false);
    setTime(0);
    setDuration(0);
    setBuffered(0);
    setFailed(false);
    setPosterFailed(false);
  }, [src, autoPlay]);

  React.useEffect(() => {
    const video = videoRef.current;
    if (!started || !video) return;
    let alive = true;
    video.play().catch(() => {
      // Autoplay with sound is refused unless the tab has been interacted
      // with. A muted retry is the browser's own escape hatch.
      if (!alive || !video.paused) return;
      video.muted = true;
      video.play().catch(() => {});
    });
    return () => {
      alive = false;
    };
  }, [started, src]);

  React.useEffect(() => {
    const video = videoRef.current;
    if (video) video.playbackRate = rate;
  }, [rate, started]);

  React.useEffect(() => {
    const onChange = () => setFullscreen(document.fullscreenElement === boxRef.current);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  React.useEffect(() => {
    if (!speedOpen) return;
    const close = (event: PointerEvent) => {
      if (!speedRef.current?.contains(event.target as Node)) setSpeedOpen(false);
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [speedOpen]);

  function togglePlay() {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) void video.play().catch(() => {});
    else video.pause();
  }

  function skip(by: number) {
    const video = videoRef.current;
    if (video)
      video.currentTime = Math.min(Math.max(0, video.currentTime + by), video.duration || 0);
  }

  async function toggleFullscreen() {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await boxRef.current?.requestFullscreen();
    } catch {
      // Refused by the browser. There is nothing useful to tell the reader.
    }
  }

  function onKeyDown(event: React.KeyboardEvent) {
    const actions: Record<string, () => void> = {
      " ": togglePlay,
      k: togglePlay,
      ArrowLeft: () => skip(-5),
      ArrowRight: () => skip(5),
      j: () => skip(-SKIP_SECONDS),
      l: () => skip(SKIP_SECONDS),
      f: () => void toggleFullscreen(),
    };
    const run = actions[event.key];
    if (!run) return;
    event.preventDefault();
    run();
  }

  function onScrubHover(event: React.PointerEvent<HTMLDivElement>) {
    if (!duration) return;
    const rect = event.currentTarget.getBoundingClientRect();
    setHoverAt(
      Math.min(duration, Math.max(0, ((event.clientX - rect.left) / rect.width) * duration))
    );
  }

  const loading = started && (!ready || waiting);

  // No `player--fullscreen`: the `:fullscreen` pseudo-class already styles
  // that, and `fullscreen` state is kept only for the button's icon.
  const box = cn(
    "player",
    upright && "player--upright",
    !playing && "player--paused",
    (pinned || speedOpen) && "player--pinned",
    className
  );

  if (failed) {
    return (
      <div ref={boxRef} className={box}>
        <div className="player__notice">
          <CircleAlert aria-hidden />
          <p className="player__notice-title">That clip could not be played</p>
          <p className="player__notice-body">
            The upload may not have finished, or the link may have expired. Reloading the page signs
            a fresh one.
          </p>
          <button
            type="button"
            className="player__retry"
            onClick={() => {
              setFailed(false);
              setStarted(true);
            }}
          >
            <RefreshCw aria-hidden /> Try again
          </button>
        </div>
      </div>
    );
  }

  // The poster gate. The clip is not requested until this is pressed.
  if (!started) {
    return (
      <div ref={boxRef} className={box}>
        <button
          type="button"
          className="player__poster"
          onClick={() => setStarted(true)}
          aria-label={`Play ${label}`}
        >
          {poster && !posterFailed && (
            <img
              src={poster}
              alt=""
              className="player__poster-image"
              onLoad={(event) =>
                setUpright(event.currentTarget.naturalHeight > event.currentTarget.naturalWidth)
              }
              onError={() => setPosterFailed(true)}
            />
          )}
          <span className="player__play">
            <Play aria-hidden fill="currentColor" />
          </span>
        </button>
      </div>
    );
  }

  return (
    <div
      ref={boxRef}
      className={box}
      tabIndex={0}
      onKeyDown={onKeyDown}
      onPointerUp={(event) => {
        if (event.pointerType === "touch") setPinned((value) => !value);
      }}
    >
      <video
        ref={videoRef}
        src={src}
        poster={poster ?? undefined}
        className="player__video"
        preload="auto"
        autoPlay
        playsInline
        muted={muted}
        onClick={togglePlay}
        onDoubleClick={() => void toggleFullscreen()}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onWaiting={() => setWaiting(true)}
        onLoadStart={() => setReady(false)}
        onPlaying={() => {
          setWaiting(false);
          setReady(true);
        }}
        onCanPlay={() => {
          setWaiting(false);
          setReady(true);
        }}
        onLoadedData={() => setReady(true)}
        onTimeUpdate={(event) => setTime(event.currentTarget.currentTime)}
        onLoadedMetadata={(event) => {
          setDuration(event.currentTarget.duration);
          // The clip's own dimensions supersede the poster's - a thumbnail
          // written by a different pass could disagree with it.
          setUpright(event.currentTarget.videoHeight > event.currentTarget.videoWidth);
        }}
        onProgress={(event) => {
          const ranges = event.currentTarget.buffered;
          if (ranges.length) setBuffered(ranges.end(ranges.length - 1));
        }}
        onEnded={() => setPlaying(false)}
        onError={() => {
          setFailed(true);
          setPlaying(false);
        }}
      />

      {/* The clip is fetched on demand, so there is a real gap between the
          press and the first frame - spin through it rather than sit on a
          still that reads as a click that did not land. */}
      {loading && <span className="player__spinner" aria-hidden />}

      {!playing && !loading && (
        <button
          type="button"
          className="player__center-play"
          onClick={togglePlay}
          aria-label={`Play ${label}`}
        >
          <span className="player__play">
            <Play aria-hidden fill="currentColor" />
          </span>
        </button>
      )}

      <div className="player__ui">
        <div
          className="player__scrub"
          onPointerMove={onScrubHover}
          onPointerLeave={() => setHoverAt(null)}
        >
          <div className="player__track">
            <div className="player__track-buffer" style={{ width: pct(buffered) }} />
            <div className="player__track-fill" style={{ width: pct(time) }} />
          </div>
          <span className="player__knob" style={{ insetInlineStart: pct(time) }} />
          {hoverAt !== null && (
            <span className="player__tip tnum" style={{ insetInlineStart: pct(hoverAt) }}>
              {formatTime(hoverAt)}
            </span>
          )}
          {/* The real control, laid transparent over the drawn one: keyboard
              and screen readers get seeking for free. */}
          <input
            type="range"
            className="player__scrub-input"
            min={0}
            max={duration || 0}
            step={0.01}
            value={time}
            onChange={(event) => {
              const to = parseFloat(event.target.value);
              setTime(to);
              if (videoRef.current) videoRef.current.currentTime = to;
            }}
            aria-label="Seek"
          />
        </div>

        <div className="player__bar">
          <button
            type="button"
            className="player__btn"
            onClick={togglePlay}
            aria-label={playing ? "Pause" : "Play"}
          >
            {playing ? (
              <Pause aria-hidden fill="currentColor" />
            ) : (
              <Play aria-hidden fill="currentColor" />
            )}
          </button>
          <button
            type="button"
            className="player__btn"
            onClick={() => skip(-SKIP_SECONDS)}
            aria-label={`Back ${SKIP_SECONDS} seconds`}
          >
            <RotateCcw aria-hidden />
          </button>
          <button
            type="button"
            className="player__btn"
            onClick={() => skip(SKIP_SECONDS)}
            aria-label={`Forward ${SKIP_SECONDS} seconds`}
          >
            <RotateCw aria-hidden />
          </button>

          <span className="player__time tnum">
            {formatTime(time)} <i>/</i> {formatTime(duration)}
          </span>

          <span className="player__gap" />

          <div className="player__speed" ref={speedRef}>
            <button
              type="button"
              className="player__btn player__speed-btn"
              onClick={() => setSpeedOpen((open) => !open)}
              aria-label="Playback speed"
              aria-expanded={speedOpen}
            >
              {rate}&times;
            </button>
            {speedOpen && (
              <div className="player__speed-menu" role="menu">
                {SPEEDS.map((speed) => (
                  <button
                    key={speed}
                    type="button"
                    role="menuitemradio"
                    aria-checked={rate === speed}
                    className={cn(rate === speed && "is-on")}
                    onClick={() => {
                      setRate(speed);
                      setSpeedOpen(false);
                    }}
                  >
                    {speed}&times;
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            type="button"
            className="player__btn"
            onClick={() => void toggleFullscreen()}
            aria-label={fullscreen ? "Leave full screen" : "Full screen"}
          >
            {fullscreen ? <Minimize2 aria-hidden /> : <Maximize2 aria-hidden />}
          </button>
        </div>
      </div>
    </div>
  );
}
